/**
 * The rules of a share-ready recording, kept apart from the browser APIs that
 * carry them out (`play-recorder.ts`) so they can be tested on their own.
 *
 * A recording is made in two steps. While the coach records, the board logs
 * what it shows on every animation frame, and the microphone is kept as raw
 * samples — nothing is encoded, so nothing can fall behind on a slow machine.
 * When they stop, the log is replayed into a fixed frame at exactly 60 fps and
 * encoded with no clock to keep up with: every frame is in the file, in every
 * browser. The format is the one every phone, computer and messenger plays:
 * H.264 + AAC in a fast-start MP4.
 */

/** The video every recording becomes. */
export const OUTPUT = {
  width: 1920,
  height: 1080,
  fps: 60,
  videoBitrate: 12_000_000,
  audioBitrate: 192_000,
  keyFrameSeconds: 2,
} as const;

/**
 * H.264 profiles in order of preference, each at level 4.2 (enough for
 * 1080p60): High, then Main, then Baseline. All three play everywhere; High
 * gives the sharpest lines for the bitrate.
 */
export const AVC_CANDIDATES: readonly string[] = ['avc1.64002A', 'avc1.4D002A', 'avc1.42002A'];

/** The first H.264 profile the browser's encoder accepts, or nothing. */
export async function pickAvc(isSupported: (codec: string) => Promise<boolean>): Promise<string | undefined> {
  for (const codec of AVC_CANDIDATES) {
    if (await isSupported(codec)) return codec;
  }
  return undefined;
}

/** How many 1/`fps` s frames a recording of `seconds` gets — at least one. */
export function slotCount(seconds: number, fps: number): number {
  return Math.max(1, Math.round(seconds * fps));
}

export interface LoggedFrame<F> {
  /** Seconds since the recording started. */
  readonly t: number;
  readonly frame: F;
}

/**
 * The board as it was at `t`: the last frame logged by then — or the first,
 * for the moment before anything was logged. `log` is in time order.
 */
export function frameAt<F>(log: readonly LoggedFrame<F>[], t: number): LoggedFrame<F> {
  let low = 0;
  let high = log.length - 1;
  // Binary search for the last entry at or before `t`.
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (log[middle]!.t <= t) low = middle;
    else high = middle - 1;
  }
  return log[low]!;
}

/**
 * Lines the voice up with the board: `offsetSeconds` is when the first sample
 * was captured, measured from the start of the recording. Captured late, it is
 * preceded by silence; early, the excess is cut. Every channel comes back
 * exactly `durationSeconds` long, padded with silence at the end if need be.
 */
export function alignAudio(
  channels: readonly Float32Array[],
  sampleRate: number,
  offsetSeconds: number,
  durationSeconds: number,
): Float32Array[] {
  const length = Math.round(durationSeconds * sampleRate);
  const shift = Math.round(offsetSeconds * sampleRate);
  return channels.map((channel) => {
    const out = new Float32Array(length);
    const from = Math.max(0, -shift);
    const to = Math.min(channel.length, from + length - Math.max(0, shift));
    if (to > from) out.set(channel.subarray(from, to), Math.max(0, shift));
    return out;
  });
}

/** How long preparing may go without a single new frame before it counts as stalled. */
export const STALL_MS = 10_000;

/** Preparing a video stopped making progress — the encoder no longer answers. */
export class PrepareStalled extends Error {
  constructor() {
    super('Preparing the video stalled.');
    this.name = 'PrepareStalled';
  }
}

/**
 * Runs `work`, which calls `progressed` whenever it gets further, and gives up
 * with `PrepareStalled` once `stallMs` pass without that. A browser's encoder
 * can stop answering (seen once in seven test runs); a promise waiting on it
 * would wait forever, and the coach with it. A real failure passes through.
 */
export function withStallGuard<T>(work: (progressed: () => void) => Promise<T>, stallMs: number = STALL_MS): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let last = Date.now();
    let settled = false;
    const watch = setInterval(() => {
      if (settled || Date.now() - last < stallMs) return;
      settled = true;
      clearInterval(watch);
      reject(new PrepareStalled());
    }, Math.min(1000, stallMs / 4));
    work(() => {
      last = Date.now();
    }).then(
      (value) => {
        if (settled) return;
        settled = true;
        clearInterval(watch);
        resolve(value);
      },
      (error: unknown) => {
        if (settled) return;
        settled = true;
        clearInterval(watch);
        reject(error);
      },
    );
  });
}
